/**
 * Conversations (M3, D-617). Mirrors `ConversationEndpoints.cs` and
 * `MessageEndpoints.cs`; the server owns every rule, this only asks.
 *
 * **Every refusal is a 404** (D-281): a conversation she is not in, a person
 * she may not reach, and a host with the module switched off all read the same
 * way, on purpose. So a 404 here is rendered as *not here*, never as *not
 * allowed* - the second wording is the leak the server took care not to make.
 */

import type { QueryClient } from '@tanstack/react-query'

import type { MessageAttachment } from './attachments'

export type ConversationKind = 'Direct' | 'Group'

export type ReplyMode = 'Shared' | 'Private'

/** Somebody a conversation may be started with, from `/reach`. */
export type Reachable = { accountId: string; name: string; kind: 'Staff' | 'Parent' | 'Dancer' | string }

/** One row in the list. Named by the other person, or the group's title. */
export type ConversationRow = {
  id: string
  kind: ConversationKind
  title: string
  lastLine: string | null
  lastAt: string | null
  unread: number
  isMuted: boolean
  /**
   * The line a search matched, when the list was filtered by one.
   *
   * Null when not searching — and null when the term matched the person's name
   * rather than anything said, because quoting an unrelated line to fill the
   * space is worse than quoting none.
   */
  match?: string | null
}

export type ConversationPerson = { accountId: string; name: string; kind: string }

/**
 * Another guardian on the same household, and the conversation I already have
 * with her if there is one (D-617). Null `conversationId` is the whole point of
 * the row: it becomes *and you have not spoken to him*, with an offer.
 */
export type HouseholdGuardian = { accountId: string; name: string; conversationId: string | null }

export type ConversationView = {
  id: string
  kind: ConversationKind
  replyMode: ReplyMode
  title: string
  people: ConversationPerson[]
  isMuted: boolean
  lastReadAt: string | null
  household: HouseholdGuardian[]
  createdByAccountId: string
  /** Whether the reader made it: the creator's reply to a private group goes to everybody (D-618). */
  isCreator: boolean
  /** The stored descriptor a group was made from; null on a direct conversation. */
  audience: string | null
  /** How many *Refresh the roster* would add; null when the reader may not press it. */
  wouldAdd: number | null
  /** The reactions this studio offers, in the order the chips draw them. */
  reactionsOffered: ReactionOffered[]
  /**
   * The message held at the top of the chat, if there is one (D-692, item 34).
   *
   * **The whole message, not an id.** The banner sits above a transcript that
   * may not have scrolled back far enough to hold it, so an id would send this
   * screen looking for something it does not have.
   *
   * **Absent covers every reason it should not show**: nothing pinned, the
   * message has since been hidden (D-621), or it was said before this reader
   * joined the group (D-617). The server applies all three, so there is nothing
   * to re-check here.
   */
  pinned?: Message | null
  /**
   * Whether to offer the pin at all (D-692).
   *
   * The server decides, from the same function the route obeys - a control that
   * refuses the person holding it is worse than no control.
   */
  mayPin?: boolean
}

export type Message = {
  id: string
  conversationId: string
  authorAccountId: string | null
  authorName: string
  /** Null on a hidden message: the row stays, the words do not (D-621). */
  body: string | null
  sentAt: string
  isSystem: boolean
  isEdited: boolean
  isHidden: boolean
  inReplyToMessageId: string | null
  isMine: boolean
  /** Only the reactions somebody has given, in the studio's own order. */
  reactions: Reaction[]
  /** Whether this message names the reader (D-628). */
  mentionsMe: boolean
  /** Why it was hidden - sent to a reviewer only, so its presence is the reviewer's view. */
  hiddenReason: string | null
  /** The thing a card points at and its live state (D-630); null on an ordinary message. */
  subject: MessageSubject | null
  /**
   * How many of the people it went to have read it (D-693, item 21).
   *
   * **Absent on anything that is not my own message**, on a member's reply in
   * somebody else's group, and on a hidden one. The server decides; this screen
   * renders what it is given and asks no questions, because a receipt is a hole
   * in other people's privacy and it must open in exactly one place.
   *
   * **Never names anybody**, which is the design rather than an omission: the
   * creator learns whether her question landed, and nothing about which parent.
   */
  seen?: SeenBy | null
  /**
   * The pictures it carries (D-751), or none.
   *
   * **Empty on a hidden message unless the reader may review**, exactly as
   * `body` is null there - the server decides and this screen renders what it
   * is given. The bytes are fetched from `attachmentUrl`, built from these
   * ids; nothing here is ever a storage address.
   */
  attachments?: MessageAttachment[] | null
}

/** A read receipt: a count and a total, never names (D-693). */
export type SeenBy = { count: number; of: number }

/**
 * A card's pointer and its live state (D-630): read on every read, never
 * stored. A cover card points at the absence and follows whoever is asked now.
 */
export type MessageSubject = {
  type: 'Ask' | 'Absence' | string
  id: string
  kind: string
  state: 'open' | 'yes' | 'no' | 'withdrawn' | 'countered' | 'unasked' | 'gone' | string
  answeredAt: string | null
}

/**
 * One door the composer may offer (D-630) - cover, task, private-lesson,
 * makeup, absence - and the words, whole and in any case, that offer it. The
 * server decides which doors from who she is; the words are the studio's.
 */
export type ComposeKind = { kind: string; triggers: string[] }

/** What the composer may offer (D-630): the doors this account may open. */
export type ComposeOffer = { kinds: ComposeKind[] }

export const COMPOSE_KEY = ['conversations', 'compose'] as const

export type MessagePage = { messages: Message[]; hasMore: boolean }

/** One reaction the studio offers, by its stable key and the glyph it draws. */
export type ReactionOffered = { key: string; glyph: string }

/** How many reacted with one key, and whether I am among them. */
export type Reaction = { emoji: string; count: number; mine: boolean }

/** One version of a message: the words, when they were written, and by whom. */
export type HistoryEntry = {
  sequence: number
  body: string
  writtenAt: string
  byAccountId: string | null
  byName: string
}

/** Every version a message has had, oldest first; the last entry is what it says now (D-621). */
export type MessageHistory = {
  messageId: string
  conversationId: string
  isHidden: boolean
  hiddenReason: string | null
  entries: HistoryEntry[]
}

export const CONVERSATIONS_KEY = ['conversations'] as const

export const conversationKey = (id: string) => ['conversations', id] as const

export const messagesKey = (id: string) => ['conversations', id, 'messages'] as const

export const REACH_KEY = ['conversations', 'reach'] as const

/**
 * How many conversations hold something unread (D-638).
 *
 * Its own key rather than a sum over `CONVERSATIONS_KEY`, because the rail asks
 * on every signed-in page and the list loads every visible message to build its
 * rows. Same reason the server gave it its own route.
 */
export const UNREAD_KEY = ['conversations', 'unread'] as const

/** What the rail puts on Messages. */
export type UnreadSummary = { conversations: number }

/**
 * Everything that changes when a conversation does (D-638).
 *
 * <b>One function because the list key is a prefix of five others.</b>
 * `CONVERSATIONS_KEY` is `['conversations']`, and `conversationKey`,
 * `messagesKey`, `REACH_KEY`, `AUDIENCES_KEY` and `COMPOSE_KEY` all sit under
 * it - so every invalidation has to pass `exact: true` or it throws away the
 * open transcript as well. That is correct, and it is also why the rail's
 * unread key was invalidated by **nothing in the repository** on the day it was
 * added: six call sites all said `exact: true`, and the seventh thing to
 * refresh was not one of them.
 *
 * The shape of the bug is worth keeping: the badge read 3, she opened all
 * three, the bell cleared, the list cleared, and the badge stayed at 3 until a
 * reload - because `NavRail` is mounted outside the router and never remounts,
 * `refetchOnWindowFocus` is off, and a stale query with no observer trigger
 * never refetches. A badge that cannot reach zero is a badge nobody believes.
 *
 * So callers say <i>a conversation changed</i> and this decides what that
 * means. A seventh thing to refresh is added here, once.
 */
export function refreshConversations(client: QueryClient): void {
  client.invalidateQueries({ queryKey: CONVERSATIONS_KEY, exact: true })
  client.invalidateQueries({ queryKey: UNREAD_KEY })
}

export const AUDIENCES_KEY = ['conversations', 'audiences'] as const

/**
 * One audience a group may be made from (D-618): a class's parents, all of a
 * teacher's parents, all teachers, all parents. `label` is the class's stored
 * name or the teacher's, never a sentence - the screen composes the words.
 */
export type AudienceDescriptor = {
  audience: 'class-parents' | 'team' | 'my-parents' | 'all-teachers' | 'all-parents' | 'people' | string
  key: string | null
  label: string
  people: number
  /** When a class meets, so two sections of one style can be told apart. Null for studio-wide audiences. */
  weekday: string | null
  startsAt: string | null
}

export type AudiencePeople = {
  audience: string
  key: string | null
  label: string
  people: ConversationPerson[]
}

export type RosterRefresh = { added: number; names: string[] }

/**
 * The supervisory view (D-626, D-629): whose conversations these are and the
 * list as she sees it. `accountId` is null for a dancer with no login, whose
 * list is empty rather than refused.
 */
export type ReviewList = { accountId: string | null; name: string; firstName: string; conversations: ConversationRow[] }

/** A conversation's header for a reader outside it: no mute, no household line. */
export type ReviewHeader = {
  id: string
  kind: ConversationKind
  replyMode: ReplyMode
  title: string
  people: ConversationPerson[]
  /** Whose seat the transcript is read from; her messages are the ones on the right. */
  subjectAccountId: string
  subjectName: string
  createdByAccountId: string
  reactionsOffered: ReactionOffered[]
}

export type ReviewTranscript = { header: ReviewHeader; page: MessagePage }

/**
 * Which door: the studio's read of an adult's account - a staff member's or a
 * parent's - or a guardian's read of her dancer's (D-631). The path segment is
 * the source, so this is also the route.
 */
export type ReviewSource = 'staff' | 'guardians' | 'dancers'

/**
 * Whether this is the studio reading somebody's whole record, or a guardian
 * reading what her dancer can see (D-631).
 *
 * **Asked once, here.** Every screen branch used to test `source === 'staff'`,
 * which silently means "not a dancer" and would have filed a parent's review
 * under the guardian's own half the moment a third source arrived.
 */
export function isStudioReview(source: ReviewSource): boolean {
  return source !== 'dancers'
}

/**
 * Where one person's supervisory screens live: the list at this path, a
 * transcript under it. One function, so the list's rows and the transcript's
 * back link cannot point at different places (D-631).
 */
export function reviewPath(source: ReviewSource, personId: string): string {
  return source === 'dancers' ? `/portal/dancers/${personId}` : `/admin/${source}/${personId}`
}

export const reviewKey = (source: ReviewSource, personId: string) =>
  ['conversations', 'review', source, personId] as const

export const reviewTranscriptKey = (source: ReviewSource, personId: string, id: string) =>
  ['conversations', 'review', source, personId, id] as const

/** The one refusal a caller needs to tell apart: gone, or not hers. */
export class NotHereError extends Error {
  constructor() {
    super('That conversation is not here')
  }
}

async function problemOf(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { title?: string; detail?: string }
    if (body.title && body.detail) return `${body.title}. ${body.detail}`
    return body.title || body.detail || fallback
  } catch {
    return fallback
  }
}

/**
 * Her conversations, optionally filtered by a search term.
 *
 * **Searching narrows the same list rather than opening a second screen** (Q5
 * of the messaging spec, which carried it unscoped). It reaches names, group
 * titles and the words of messages she can already open — never a message the
 * transcript would refuse her, and never a hidden one.
 */
export async function fetchConversations(search?: string): Promise<ConversationRow[]> {
  const term = search?.trim()
  const where = term ? `/api/conversations?q=${encodeURIComponent(term)}` : '/api/conversations'

  const res = await fetch(where)
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not load your messages (${res.status})`)
  return res.json()
}

/**
 * Never throws for the rail (D-209 on a badge).
 *
 * Every database-backed endpoint refuses for about 75 seconds after a deploy
 * (D-33), and the rail is on every page - so the honest answer to a refusal is
 * *no badge*, never a number.
 *
 * <b>What it actually saves is a retry, not a red screen.</b> An earlier
 * version of this sentence claimed a throw would put an error state in the
 * corner of every page; it would not - nothing reads this query's error state
 * and there is no global error handler - so the real effect is one avoided
 * round trip and a query that resolves rather than rejects. Worth stating
 * accurately, because the comment is the whole argument for swallowing
 * something.
 */
export async function fetchUnreadConversations(): Promise<UnreadSummary> {
  const res = await fetch('/api/conversations/unread')
  if (!res.ok) return { conversations: 0 }
  return res.json()
}

export async function fetchReach(): Promise<Reachable[]> {
  const res = await fetch('/api/conversations/reach')
  if (!res.ok) throw new Error(`Could not load who you can message (${res.status})`)
  return res.json()
}

export async function fetchConversation(id: string): Promise<ConversationView> {
  const res = await fetch(`/api/conversations/${id}`)
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not open that conversation (${res.status})`)
  return res.json()
}

/**
 * The pair's conversation, opened rather than created: 200 if it existed, 201
 * if this call made it, and the same shape either way.
 */
export async function openDirect(withAccountId: string): Promise<ConversationView> {
  const res = await fetch('/api/conversations/direct', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ withAccountId }),
  })
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(await problemOf(res, `Could not start that conversation (${res.status})`))
  return res.json()
}

export async function fetchMessages(id: string, before?: string): Promise<MessagePage> {
  const query = before ? `?before=${encodeURIComponent(before)}` : ''
  const res = await fetch(`/api/conversations/${id}/messages${query}`)
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not load the conversation (${res.status})`)
  return res.json()
}

/**
 * Posts a message. `mentions` names accounts by id; the server refuses one
 * that is not in the conversation the message lands in, and a named person is
 * told even through a mute (D-628).
 */
export async function sendMessage(
  id: string,
  body: string,
  mentions: string[] = [],
  subject: { type: string; id: string } | null = null,
  attachmentIds: string[] = [],
): Promise<{ message: Message; landedIn: string }> {
  const res = await fetch(`/api/conversations/${id}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      subject ? { body, mentions, subject, attachmentIds } : { body, mentions, attachmentIds },
    ),
  })
  if (res.status === 404) throw new NotHereError()
  if (res.status === 429) {
    const wait = res.headers.get('Retry-After')
    throw new Error(
      wait ? `That is a lot of messages at once. Try again in ${wait} seconds.` : 'That is a lot of messages at once. Try again shortly.',
    )
  }
  if (!res.ok) throw new Error(await problemOf(res, `Could not send that (${res.status})`))
  return res.json()
}

/**
 * I have read up to here. **Also clears the bell rows for this conversation**
 * on the server, in the same transaction (D-623) - which is why the caller
 * invalidates the notification queries afterwards.
 */
export async function markRead(id: string, upTo?: string): Promise<{ unread: number }> {
  const res = await fetch(`/api/conversations/${id}/read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ upTo: upTo ?? null }),
  })
  if (!res.ok) throw new Error(`Could not mark that read (${res.status})`)
  return res.json()
}

export async function setMuted(id: string, muted: boolean): Promise<{ isMuted: boolean }> {
  const res = await fetch(`/api/conversations/${id}/mute`, { method: muted ? 'POST' : 'DELETE' })
  if (!res.ok) throw new Error(`Could not ${muted ? 'mute' : 'unmute'} that (${res.status})`)
  return res.json()
}

/** The audiences this person may make a group from. Empty for a parent. */
export async function fetchAudiences(): Promise<AudienceDescriptor[]> {
  const res = await fetch('/api/conversations/audiences')
  if (!res.ok) throw new Error(`Could not load who you can send to (${res.status})`)
  return res.json()
}

export async function fetchAudiencePeople(audience: string, key: string | null): Promise<AudiencePeople> {
  const query = new URLSearchParams({ audience })
  if (key) query.set('key', key)
  const res = await fetch(`/api/conversations/audiences/people?${query}`)
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not load that audience (${res.status})`)
  return res.json()
}

/**
 * Makes a group. **Anything that is not exactly `Shared` is private** on the
 * server, and a shared group is refused unless `confirmShared` names the
 * number of people who will see every reply (D-618).
 */
export async function createGroup(body: {
  /** For the `people` audience only: who she ticked (D-726). */
  include?: string[]
  audience: string
  key: string | null
  title: string
  replyMode: ReplyMode
  exclude: string[]
  confirmShared: number | null
}): Promise<ConversationView> {
  const res = await fetch('/api/conversations/group', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (res.status === 404) throw new NotHereError()
  if (res.status === 429) throw new Error('That is a lot of groups at once. Try again shortly.')
  if (!res.ok) throw new Error(await problemOf(res, `Could not start that group (${res.status})`))
  return res.json()
}

/** Re-resolves the group's audience and adds whoever is new. Never removes anybody. */
export async function refreshRoster(id: string): Promise<RosterRefresh> {
  const res = await fetch(`/api/conversations/${id}/refresh-roster`, { method: 'POST' })
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(await problemOf(res, `Could not refresh the roster (${res.status})`))
  return res.json()
}

/**
 * The supervisory list (D-626). Every open is recorded on the server before
 * anything is served; the screen says so. A 404 is *not here* as everywhere
 * else: a staff record with no account, somebody else's dancer, and a reader
 * without the capability all read the same (D-281).
 */
export async function fetchReviewList(source: ReviewSource, personId: string): Promise<ReviewList> {
  const res = await fetch(`/api/${source}/${personId}/conversations`)
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not load those conversations (${res.status})`)
  return res.json()
}

export async function fetchReviewTranscript(
  source: ReviewSource,
  personId: string,
  id: string,
  before?: string,
): Promise<ReviewTranscript> {
  const query = before ? `?before=${encodeURIComponent(before)}` : ''
  const res = await fetch(`/api/${source}/${personId}/conversations/${id}${query}`)
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not load that conversation (${res.status})`)
  return res.json()
}

/** The words that look like a request, and the kinds this account may raise (D-630). */
export async function fetchCompose(): Promise<ComposeOffer> {
  const res = await fetch('/api/conversations/compose')
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not load the request offer (${res.status})`)
  return res.json()
}
/**
 * Edits my own message. Every version is kept (D-621); the server refuses a
 * hidden one with 409 and its own sentence, which is the one shown.
 */
export async function editMessage(id: string, messageId: string, body: string): Promise<Message> {
  const res = await fetch(`/api/conversations/${id}/messages/${messageId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  })
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(await problemOf(res, `Could not save that edit (${res.status})`))
  return res.json()
}

export async function fetchHistory(id: string, messageId: string): Promise<MessageHistory> {
  const res = await fetch(`/api/conversations/${id}/messages/${messageId}/history`)
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(`Could not load the history (${res.status})`)
  return res.json()
}

/**
 * Gives or takes back my reaction, by its key. A reaction tells nobody
 * (D-628), so nothing here touches the bell. Returns every count afresh.
 */
export async function setReaction(id: string, messageId: string, emoji: string, on: boolean): Promise<Reaction[]> {
  const base = `/api/conversations/${id}/messages/${messageId}/reactions`
  const res = on
    ? await fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      })
    : await fetch(`${base}?emoji=${encodeURIComponent(emoji)}`, { method: 'DELETE' })
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(await problemOf(res, `Could not save that reaction (${res.status})`))
  const counts = (await res.json()) as { reactions: Reaction[] }
  return counts.reactions
}

/**
 * The one remedy (D-621): the row and its versions stay, the words go for
 * everybody but a reviewer, and the reason is kept with it. 404 for anyone
 * without `ReviewConversations`, like every other refusal here.
 */
export async function hideMessage(id: string, messageId: string, reason: string): Promise<Message> {
  const res = await fetch(`/api/conversations/${id}/messages/${messageId}/hide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  if (res.status === 404) throw new NotHereError()
  if (!res.ok) throw new Error(await problemOf(res, `Could not hide that (${res.status})`))
  return res.json()
}

/**
 * Hold one message at the top of the chat, or take it off (D-692, item 34).
 *
 * **Pinning replaces; it never adds.** The server holds one id, so there is no
 * *unpin the old one first* and no way to reach two.
 *
 * **Unpinning names which one.** Two people may hold the control in a direct
 * conversation, so *clear whatever is up there* would let the second tap remove
 * a pin she never read. The answer is always what is up there now, which is
 * also the right answer when somebody else got there first.
 *
 * A 403 here is a real refusal rather than the usual 404 (D-281): she is in the
 * conversation and can read every word, so pretending the message is not there
 * would be a lie she can disprove by scrolling.
 */
export async function setPinned(
  id: string,
  messageId: string,
  on: boolean,
): Promise<{ pinnedMessageId: string | null }> {
  const url = `/api/conversations/${id}/messages/${messageId}/pin`
  const res = await fetch(url, { method: on ? 'POST' : 'DELETE' })

  /*
   * **Not `NotHereError` here, unlike every other call in this file.** Its
   * sentence is *that conversation is not here*, and she is looking at the
   * conversation — the realistic 404 is that the message was hidden between
   * the screen rendering and her tapping (D-621), so that wording would send
   * her hunting for the wrong thing (D-209).
   */
  if (res.status === 404) {
    throw new Error('That message is no longer there — it may have been hidden.')
  }

  /*
   * **A 403 has no problem body**, because the server answers `Forbid()`. The
   * fallback would be a status code, and the rest of this module renders
   * sentences.
   */
  if (res.status === 403) {
    throw new Error('Only whoever set this conversation up can move what is pinned.')
  }

  if (!res.ok) {
    throw new Error(
      await problemOf(
        res,
        on ? `Could not pin that (${res.status})` : `Could not unpin that (${res.status})`,
      ),
    )
  }
  return res.json()
}

/**
 * When a message was sent, as a list row shows it: the time today, the weekday
 * this week, the date otherwise. Pure, so it is testable without a clock.
 */
export function whenLabel(iso: string, now: Date): string {
  const at = new Date(iso)
  const sameDay = at.toDateString() === now.toDateString()

  if (sameDay) return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

  const days = (now.getTime() - at.getTime()) / 86_400_000

  if (days < 6.5) return at.toLocaleDateString(undefined, { weekday: 'short' })

  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/**
 * Groups the picker: who she may reach, by kind. The server says who; this
 * only decides the grouping, and it never invents a person. Each group names
 * the content block that heads it (A-16), with the fallback the seed carries.
 */
export function groupReach(
  reach: Reachable[],
): { key: string; fallback: string; people: Reachable[] }[] {
  const staff = reach.filter((r) => r.kind === 'Staff')
  const parents = reach.filter((r) => r.kind === 'Parent')
  // A dancer's own login, under its own heading (D-629): a teacher should
  // never have to guess whether a name is a parent or her child.
  const dancers = reach.filter((r) => r.kind === 'Dancer')
  const rest = reach.filter((r) => r.kind !== 'Staff' && r.kind !== 'Parent' && r.kind !== 'Dancer')

  return [
    { key: 'messages.pick.staff', fallback: 'Staff', people: staff },
    { key: 'messages.pick.parents', fallback: 'Parents', people: parents },
    { key: 'messages.pick.dancers', fallback: 'Dancers', people: dancers },
    { key: 'messages.pick.other', fallback: 'Other', people: rest },
  ].filter((g) => g.people.length > 0)
}

/** A search over the picker, by any part of the name, case-insensitively. */
export function matchesReach(person: { name: string }, query: string): boolean {
  const q = query.trim().toLowerCase()
  return q.length === 0 || person.name.toLowerCase().includes(q)
}

/**
 * The mention being typed, if any: an `@` at the start of the text or after
 * a space, with no line break between it and the caret. Null otherwise,
 * which is what closes the picker - so an email address mid-word offers
 * nobody, and a finished mention on the line above does not reopen it.
 */
export function mentionQuery(draft: string, caret: number): { start: number; query: string } | null {
  const upTo = draft.slice(0, caret)
  const at = upTo.lastIndexOf('@')

  if (at < 0) return null
  if (at > 0 && !/\s/.test(upTo[at - 1])) return null

  const query = upTo.slice(at + 1)

  if (/[\r\n]/.test(query)) return null

  return { start: at, query }
}

/** Replaces the `@query` being typed with `@Name `, and says where the caret lands. */
export function insertMention(
  draft: string,
  start: number,
  caret: number,
  name: string,
): { draft: string; caret: number } {
  const inserted = `@${name} `

  return { draft: draft.slice(0, start) + inserted + draft.slice(caret), caret: start + inserted.length }
}

/**
 * Who the draft names: every person whose `@Name` appears in it, whole and
 * in any case - a phone keyboard lowercases mid-sentence. Read off the words
 * at send time rather than remembered from the picker, so deleting the name
 * deletes the mention. Where one name begins another (`Sam`, `Sam Jones`),
 * the longer wins at that spot, so `@Sam Jones` names one person; two people
 * with one identical name are both named, because the words cannot say
 * which and the server takes a list.
 */
export function mentionsIn(draft: string, people: { accountId: string; name: string }[]): string[] {
  const text = draft.toLowerCase()
  const claimed = new Map<number, number>()
  const named = new Set<string>()

  const byLength = people
    .map((p, order) => ({ p, order, name: p.name.trim() }))
    .filter((x) => x.name.length > 0)
    .sort((a, b) => b.name.length - a.name.length || a.order - b.order)

  for (const { p, name } of byLength) {
    const token = `@${name.toLowerCase()}`
    let from = 0

    while (from <= text.length) {
      const at = text.indexOf(token, from)
      if (at < 0) break

      const after = text[at + token.length]
      const whole = after === undefined || !/\p{L}|\p{N}/u.test(after)
      const longer = claimed.get(at)

      if (whole && (longer === undefined || longer <= token.length)) {
        claimed.set(at, token.length)
        named.add(p.accountId)
      }

      from = at + 1
    }
  }

  // In the conversation's order, not by length.
  return people.filter((p) => named.has(p.accountId)).map((p) => p.accountId)
}

/**
 * Whether the draft looks like a request (D-630): one of the trigger words,
 * whole, in any case, anywhere in it. `recover` is not `cover` and
 * `cover-up` is not either; punctuation beside the word is fine. No word
 * ever creates anything - this only decides whether to offer.
 */
export function looksLikeARequest(draft: string, triggers: string[]): boolean {
  const text = draft.toLowerCase()
  if (text.trim().length === 0) return false

  return triggers.some((trigger) => {
    const word = trigger.trim().toLowerCase()
    if (word.length === 0) return false

    let from = 0

    while (from <= text.length) {
      const at = text.indexOf(word, from)
      if (at < 0) return false

      const before = at === 0 ? undefined : text[at - 1]
      const after = text[at + word.length]
      const boundary = (c: string | undefined) => c === undefined || !/[\p{L}\p{N}-]/u.test(c)

      if (boundary(before) && boundary(after)) return true

      from = at + 1
    }

    return false
  })
}

/**
 * The doors the draft looks like it wants, in the order the server offers
 * them (D-630). Each door has its own words, so "makeup" offers the makeup
 * and not every door she may open; an offer that has not arrived offers
 * nothing rather than everything.
 */
export function offeredKinds(draft: string, offer: ComposeOffer | undefined): string[] {
  if (!offer?.kinds) return []

  return offer.kinds.filter((k) => looksLikeARequest(draft, k.triggers)).map((k) => k.kind)
}

/** A door's content-block suffix: `private-lesson` reads `privateLesson`, as every other key does. */
export function chipKey(kind: string): string {
  return kind.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
}

/**
 * Her words as a task (D-630): the first line is the title, clipped to what
 * a row can show, and the whole draft is the note only when the title did
 * not already hold all of it - a task saying the same thing twice reads as
 * a mistake.
 */
export function taskFrom(draft: string): { title: string; note: string } {
  const words = draft.trim()
  const firstLine = (words.split('\n')[0] ?? '').trim()
  const title = firstLine.length > 80 ? firstLine.slice(0, 77).trimEnd() + '...' : firstLine

  return { title, note: title === words ? '' : words }
}
